/**
 * PieceCard — Compact list row with colored initial square.
 * Layout: [40px square] [title + dims·medium] [price + $/hr]
 * Square color: binary — teal (above floor) or coral (below floor).
 */

import { useRef, useState } from 'react';
import { T } from '@/styles/tokens';
import { isAboveFloor } from '@/lib/floorCheck';
import type { VerdictSummary } from '@/types';

interface PieceCardProps {
  item: VerdictSummary;
  floorHourly: number;
  index: number;
  visible: boolean;
  selectionMode: boolean;
  selected: boolean;
  onToggle: () => void;
  onLongPress: () => void;
  onTap: () => void;
  isDragging: boolean;
  isDropTarget: boolean;
}

function displayPrice(item: VerdictSummary): string {
  const cents = item.sold && item.soldPrice ? item.soldPrice : (item.gutPrice ?? item.ttcp);
  return `$${Math.round(cents / 100).toLocaleString()}`;
}

export function PieceCard({
  item, floorHourly, index, visible,
  selectionMode, selected, onToggle, onLongPress, onTap,
  isDragging,
}: PieceCardProps) {
  const longTimer = useRef<ReturnType<typeof setTimeout> | null>(null);
  const moved = useRef(false);
  const [isPressed, setIsPressed] = useState(false);

  const down = () => {
    setIsPressed(true);
    if (selectionMode) return;
    moved.current = false;
    longTimer.current = setTimeout(() => {
      if (!moved.current) onLongPress();
    }, 450);
  };
  const up = () => { setIsPressed(false); if (longTimer.current) clearTimeout(longTimer.current); };
  const mv = () => { moved.current = true; if (longTimer.current) { clearTimeout(longTimer.current); longTimer.current = null; } };

  const click = () => {
    if (selectionMode) { onToggle(); return; }
    onTap();
  };

  const title = item.title || `${item.width}\u00D7${item.height}"`;
  const isSold = item.sold;
  const mediumLabel = item.medium ? item.medium.replace('_', ' ') : '';
  const above = isAboveFloor({ gutPrice: item.gutPrice, truePrice: item.ttcp, floor: item.ttcp });

  // Binary: light square, dark letter
  const squareBg = above ? '#E1F5EE' : '#FEF3EB';
  const squareText = above ? T.primary : T.warning;      // #3DABA5 / #E07A3D

  return (
    <div
      className="tp-card"
      onPointerDown={down}
      onPointerUp={up}
      onPointerLeave={up}
      onPointerMove={mv}
      onContextMenu={e => e.preventDefault()}
      onClick={click}
      style={{
        display: 'flex',
        alignItems: 'center',
        gap: 14,
        padding: '14px 0',
        cursor: 'pointer',
        background: isDragging ? 'rgba(61,171,165,0.04)' : selected ? 'rgba(61,171,165,0.03)' : 'transparent',
        opacity: visible ? (isDragging ? 0.92 : isSold ? 0.55 : 1) : 0,
        transform: visible
          ? (isDragging ? 'scale(1.02)' : isPressed ? 'scale(0.99)' : 'translateY(0)')
          : 'translateY(8px)',
        transition: isDragging
          ? 'transform 0.15s'
          : `opacity 0.3s ease ${index * 20}ms, transform 120ms ease, background 0.15s`,
        zIndex: isDragging ? 50 : 1,
        position: 'relative',
      }}
    >
      {/* Avatar square / selection checkbox */}
      <div
        onClick={e => {
          e.stopPropagation();
          if (selectionMode) { onToggle(); } else { onLongPress(); }
        }}
        style={{
          width: 40, height: 40, borderRadius: 8,
          background: selected ? T.primary : squareBg,
          display: 'flex', alignItems: 'center', justifyContent: 'center',
          flexShrink: 0, cursor: 'pointer',
          transition: 'background 0.2s',
        }}
      >
        {selected
          ? <svg width="16" height="16" viewBox="0 0 14 14" fill="none">
              <path d="M3 7.5l2.5 2.5L11 4" stroke={T.white} strokeWidth="2" strokeLinecap="round" strokeLinejoin="round" />
            </svg>
          : <span style={{
              fontSize: 16, fontWeight: 500,
              color: squareText,
              fontFamily: T.fontFamily, lineHeight: 1, userSelect: 'none',
            }}>
              {title.charAt(0).toUpperCase()}
            </span>
        }
      </div>

      {/* Text column */}
      <div style={{ flex: 1, minWidth: 0 }}>
        <div style={{
          fontSize: 15, fontWeight: 500, color: T.text,
          fontFamily: T.fontFamily,
          overflow: 'hidden', textOverflow: 'ellipsis', whiteSpace: 'nowrap',
        }}>
          {title}
        </div>
        <div style={{
          fontSize: 12, color: T.textMuted, fontFamily: T.fontFamily,
          marginTop: 2,
        }}>
          {item.width}&times;{item.height}&quot;
          {mediumLabel && <>{' '}<span style={{ color: '#D1D5DB' }}>&middot;</span>{' '}<span style={{ textTransform: 'capitalize' }}>{mediumLabel}</span></>}
        </div>
        {item.hadFreeRoast && (
          <div style={{ fontSize: 11, color: T.primary, fontFamily: T.fontFamily, marginTop: 3, fontWeight: 600 }}>
            ✦ Forensic Roast included
          </div>
        )}
      </div>

      {/* Price column */}
      <div style={{ textAlign: 'right', flexShrink: 0 }}>
        <div style={{
          fontSize: 16, fontWeight: 500, color: T.text,
          fontFamily: T.fontFamily, fontVariantNumeric: 'tabular-nums',
        }}>
          {displayPrice(item)}
        </div>
        {item.effectiveHourly != null && (
          <div style={{
            fontSize: 12, color: T.textMuted, fontFamily: T.fontFamily,
            fontVariantNumeric: 'tabular-nums', marginTop: 1,
          }}>
            ${Math.round(item.effectiveHourly / 100)}/hr
          </div>
        )}
      </div>
    </div>
  );
}
